import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  getOzonetelDialerMode,
  isOzonetelIframeDialer,
  isOzonetelSdkDialer,
  OzonetelDialerMode,
} from './ozonetel-dialer-mode';

export interface OzonetelCaseData {
  dueId?: number | string;
  DueId?: number | string;
  phoneNo?: string;
  phoneNumber?: string;
  fullName?: string;
  Status?: string;
  status?: string;
  defaulterId?: number | string;
  ucid?: string;
  UCID?: string;
  did?: string;
  DID?: string;
  callType?: string;
  campaignName?: string;
  communicationDetailId?: number | string;
  commDetId?: number | string;
  clientId?: string;
  callingType?: string;
  isToggled?: boolean;
  pauseCall?: boolean;
  holdCall?: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class OzonetelDialerFlowService {
  private lastNavigationKey = '';
  private lastNavigationAt = 0;

  constructor(private http: HttpClient, private router: Router) { }

  getMode(): OzonetelDialerMode {
    return getOzonetelDialerMode();
  }

  isSdkMode(): boolean {
    return isOzonetelSdkDialer();
  }

  isIframeMode(): boolean {
    return isOzonetelIframeDialer();
  }

  /**
   * Ozonetel toolbar custData entries use "Field~value" (e.g. Name~Karan Pal, PhoneNumber~8869012507).
   */
  parseCustDataField(custData: unknown, fieldName: string): string {
    if (custData == null) {
      return '';
    }
    const list = Array.isArray(custData) ? custData : [custData];
    const prefix = `${fieldName}~`;
    for (const item of list) {
      const s = String(item).trim();
      if (s.startsWith(prefix)) {
        return s.slice(prefix.length).trim();
      }
    }
    return '';
  }

  private custDataNameFieldNames = [
    'Name',
    'CustomerName',
    'CUSTOMER_NAME',
    'CustName',
    'DefaulterName',
    'Customer_Name',
  ];

  parseCustDataName(custData: unknown): string {
    for (const field of this.custDataNameFieldNames) {
      const value = this.parseCustDataField(custData, field);
      if (value && value.toLowerCase() !== 'unknown') {
        return value;
      }
    }
    return '';
  }

  resolveFullName(data: any): string {
    if (!data || typeof data !== 'object') {
      return '';
    }
    const candidates = [
      data.fullName,
      data.name,
      data.customerName,
      data.CustomerName,
      data.callerName,
      data.custName,
      data.displayName,
      this.parseCustDataName(data.custData),
    ];
    for (const name of candidates) {
      const trimmed = name != null ? String(name).trim() : '';
      if (trimmed && trimmed.toLowerCase() !== 'unknown') {
        return trimmed;
      }
    }
    return '';
  }

  getPhoneNumber(data: any): string {
    const custPhone = this.parseCustDataField(data?.custData, 'PhoneNumber');
    const phoneNumber =
      custPhone ||
      data?.phoneNo ||
      data?.phoneNumber ||
      data?.callerId ||
      data?.callerID ||
      data?.customerNumber ||
      data?.CustomerNumber ||
      data?.did ||
      data?.DID;

    return phoneNumber != null ? String(phoneNumber) : '';
  }

  /** Merges toolbar custData and latest signals into SDK event payloads for the dialer UI. */
  enrichCallDisplayData(data: any): OzonetelCaseData {
    if (!data || typeof data !== 'object') {
      return data;
    }
    const fullName = this.resolveFullName(data);
    const phoneNo = this.getPhoneNumber(data);
    const out: OzonetelCaseData = { ...data };
    if (fullName) {
      out.fullName = fullName;
    } else if (out.fullName === 'Unknown') {
      delete out.fullName;
    }
    if (phoneNo) {
      out.phoneNo = phoneNo;
      if (!out.phoneNumber) {
        out.phoneNumber = phoneNo;
      }
    }
    return out;
  }

  getUcid(data: any): string {
    return data?.ucid || data?.UCID || data?.uui || data?.UUI || data?.senderServiceId || '';
  }

  getDid(data: any): string {
    return data?.did || data?.DID || data?.callerID || data?.callerId || '';
  }

  getCallKey(data: any): string {
    return [
      data?.dueId || data?.DueId || '',
      this.getUcid(data),
      this.getPhoneNumber(data),
    ].join(':');
  }

  normalizeCallData(data: OzonetelCaseData, fallbackStatus = 'CONNECTED'): OzonetelCaseData {
    const enriched = this.enrichCallDisplayData(data);
    const status = this.normalizeStatus(
      enriched?.Status || enriched?.status || enriched?.['callStatus'],
      fallbackStatus
    );
    const dueId = enriched?.dueId || enriched?.DueId;
    const phoneNo = this.getPhoneNumber(enriched);
    const ucid = this.getUcid(enriched);
    const did = this.getDid(enriched);
    const communicationDetailId = enriched?.communicationDetailId || enriched?.commDetId;
    const fullName = this.resolveFullName(enriched);

    const normalized: OzonetelCaseData = {
      ...enriched,
      dueId,
      DueId: dueId,
      phoneNo,
      phoneNumber: enriched?.phoneNumber || phoneNo,
      Status: status,
      status,
      ucid,
      did,
      communicationDetailId,
      commDetId: communicationDetailId,
      callingType: 'ozonetel',
    };
    if (fullName) {
      normalized.fullName = fullName;
    } else if (normalized.fullName === 'Unknown') {
      delete normalized.fullName;
    }
    return normalized;
  }

  async lookupCase(callData: OzonetelCaseData): Promise<OzonetelCaseData | null> {
    const phoneNumber = this.getPhoneNumber(callData);

    if (!phoneNumber) {
      console.warn('[OzonetelDialerFlow] Cannot lookup case without phone number', callData);
      return null;
    }

    const response: any = await firstValueFrom(
      this.http.post(
        `${environment.api_url}/api/ozonetel-webhook/lookup-case`,
        {
          phoneNumber,
          ucid: this.getUcid(callData),
          did: this.getDid(callData),
          callType: callData?.callType || callData?.['type'],
        },
        { withCredentials: true }
      )
    );

    if (response?.resultShort === 'success' && response.data) {
      return this.normalizeCallData(
        {
          ...callData,
          ...response.data,
          ucid: response.data.ucid || this.getUcid(callData),
          did: response.data.did || this.getDid(callData),
          Status: 'CONNECTED',
        },
        'CONNECTED'
      );
    }

    console.warn('[OzonetelDialerFlow] No case found for Ozonetel call', {
      phoneNumber,
      response,
    });
    return null;
  }

  async openCaseFromCall(callData: OzonetelCaseData, fallbackStatus = 'CONNECTED'): Promise<OzonetelCaseData | null> {
    let normalized = this.normalizeCallData(callData, fallbackStatus);

    if (!normalized.dueId) {
      const lookupResult = await this.lookupCase(normalized);
      if (!lookupResult) {
        return null;
      }
      normalized = this.normalizeCallData(lookupResult, fallbackStatus);
    }

    this.openCase(normalized);
    return normalized;
  }

  openCase(callData: OzonetelCaseData): boolean {
    const normalized = this.normalizeCallData(callData, callData?.Status || 'CONNECTED');

    if (!normalized.dueId || !this.isOpenCaseStatus(normalized.Status)) {
      return false;
    }

    const navigationKey = this.getCallKey(normalized);
    const now = Date.now();
    if (navigationKey && navigationKey === this.lastNavigationKey && now - this.lastNavigationAt < 1500) {
      return true;
    }
    this.lastNavigationKey = navigationKey;
    this.lastNavigationAt = now;

    const queryParams: any = {
      callerNo: normalized.phoneNo,
      phoneNo: normalized.phoneNo,
      fullName: normalized.fullName,
      callingType: 'ozonetel',
    };

    if (normalized.ucid) {
      queryParams.ucid = normalized.ucid;
    }
    if (normalized.did) {
      queryParams.did = normalized.did;
    }

    this.router.navigate(['/timeline', normalized.dueId], { queryParams });
    return true;
  }

  toCallingState(callData: OzonetelCaseData, status?: string): OzonetelCaseData {
    return this.normalizeCallData(
      {
        ...callData,
        Status: status || callData?.Status || callData?.status,
      },
      status || 'CONNECTED'
    );
  }

  toIdleCallingState(existing?: OzonetelCaseData): OzonetelCaseData {
    return {
      Status: 'Idle',
      callingType: existing?.callingType || 'ozonetel',
      isToggled: existing?.isToggled ?? false,
      pauseCall: false,
      holdCall: false,
      transferCall: false,
    };
  }

  private normalizeStatus(status: any, fallbackStatus: string): string {
    const value = String(status || fallbackStatus || '').toUpperCase();

    if (['CONNECTED', 'BUSY', 'ANSWERED', 'LIVE'].includes(value)) {
      return 'CONNECTED';
    }
    if (['CALLING', 'RINGING', 'NEW_CALL', 'INITIATED', 'QUEUED'].includes(value)) {
      return 'CALLING';
    }
    if (['DISCONNECTED', 'DROP', 'DROPPED', 'ENDED', 'COMPLETED'].includes(value)) {
      return 'Disconnected';
    }
    if (value === 'IDLE') {
      return 'Idle';
    }

    return fallbackStatus || 'CONNECTED';
  }

  private isOpenCaseStatus(status: string): boolean {
    return ['CONNECTED', 'CALLING'].includes(String(status || '').toUpperCase());
  }
}
